import { useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { moveQuotePosition } from "@/lib/quote-position-order";

export function ReorderableQuotePositions<T extends { id: string }>({ items, onReorder, children }: {
  items: T[];
  onReorder: (ids: string[]) => void;
  children: (item: T, index: number, controls: ReactNode) => ReactNode;
}) {
  const source = useRef<string | null>(null);
  const touchTarget = useRef<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const ids = items.map((item) => item.id);
  const finish = () => {
    source.current = null; touchTarget.current = null; setDragging(null); setTarget(null);
  };
  const move = (from: string, to: string) => {
    const next = moveQuotePosition(ids, from, to);
    if (next !== ids) {
      onReorder(next);
      setAnnouncement(`Position an Stelle ${next.indexOf(from) + 1} verschoben.`);
    }
  };
  const hit = (element: EventTarget | null) =>
    element instanceof Element ? element.closest<HTMLElement>("[data-quote-position-id]")?.dataset.quotePositionId : undefined;
  return <div className="space-y-4" role="list" aria-label="Angebotspositionen sortieren"
    onDragOver={(event) => {
      if (!source.current) return;
      event.preventDefault(); event.dataTransfer.dropEffect = "move";
      setTarget(hit(event.target) ?? null);
    }}
    onDrop={(event) => {
      if (!source.current) return;
      event.preventDefault();
      const destination = hit(event.target);
      if (destination) move(source.current, destination);
      finish();
    }}>
    {items.map((item, index) => {
      const controls = <div className="ml-auto flex items-center gap-1">
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`Position ${index + 1} nach oben`}
          disabled={index === 0} onClick={() => move(item.id, ids[index - 1])}><ArrowUp className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`Position ${index + 1} nach unten`}
          disabled={index === ids.length - 1} onClick={() => move(item.id, ids[index + 1])}><ArrowDown className="h-3.5 w-3.5" /></Button>
        <button type="button" draggable className="touch-none cursor-grab rounded p-1.5 text-muted-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-bauwerk-teal active:cursor-grabbing"
          aria-label={`Position ${index + 1} verschieben`} title="Ziehen zum Verschieben; alternativ Pfeiltasten nach oben/unten"
          aria-pressed={dragging === item.id}
          onDragStart={(event) => {
            source.current = item.id; setDragging(item.id);
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", item.id);
          }}
          onDragEnd={finish}
          onKeyDown={(event) => {
            const offset = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
            if (offset && ids[index + offset]) { event.preventDefault(); move(item.id, ids[index + offset]); }
            if (event.key === "Escape") finish();
          }}
          onPointerDown={(event) => {
            if (event.pointerType !== "touch" && event.pointerType !== "pen") return;
            event.preventDefault(); source.current = item.id; setDragging(item.id);
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!source.current || (event.pointerType !== "touch" && event.pointerType !== "pen")) return;
            const destination = hit(document.elementFromPoint(event.clientX, event.clientY));
            touchTarget.current = destination ?? null; setTarget(destination ?? null);
          }}
          onPointerUp={(event) => {
            if (!source.current || (event.pointerType !== "touch" && event.pointerType !== "pen")) return;
            if (touchTarget.current) move(source.current, touchTarget.current);
            finish();
          }}
          onPointerCancel={(event) => {
            // Native desktop HTML dragging dispatches pointercancel when it
            // takes over. Only cancel our separate touch/pen gesture here.
            if (event.pointerType === "touch" || event.pointerType === "pen") finish();
          }}><GripVertical className="h-4 w-4" /></button>
      </div>;
      return <div role="listitem" key={item.id} data-quote-position-id={item.id}
        className={target === item.id && dragging !== item.id ? "rounded-lg outline outline-2 outline-offset-2 outline-bauwerk-teal" : ""}
      >{children(item, index, controls)}</div>;
    })}
    <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
  </div>;
}
